import { CheckIcon, ChevronDownIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useController, UseControllerProps } from 'react-hook-form'

import * as Checkbox from '@radix-ui/react-checkbox'
import * as Popover from '@radix-ui/react-popover'

import { SelectInterface } from '../../interfaces/InterfacesLocal'
import { cn } from '@/lib/utils'

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ')
}

interface InputProps {
  label: string
  data: SelectInterface[]
  byDefault?: (string | number | null)[]
  disabled?: boolean
  loading?: boolean
  changeState?: (_val: SelectInterface[]) => void
  className?: string
}

export default function MultiSelectRadix(
  props: UseControllerProps & InputProps
) {
  const { field, fieldState } = useController(props)
  const { label, data, byDefault, disabled, loading, changeState } = props
  const [selected, setSelected] = useState<(string | number)[]>([])
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [triggerWidth, setTriggerWidth] = useState<number | undefined>(
    undefined
  )

  useEffect(() => {
    if (Array.isArray(byDefault) && byDefault.length > 0) {
      const filtered = byDefault.filter(
        (val): val is string | number => val !== null
      )
      setSelected(filtered)
      field.onChange(filtered)
      const selectedObjects = data.filter((item) => byDefault.includes(item.id))
      changeState?.(selectedObjects)
    } else if (byDefault && byDefault.length === 0) {
      setSelected([])
      field.onChange([])
      changeState?.([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(byDefault)])

  // Actualiza el ancho del trigger cuando cambie data o loading
  useEffect(() => {
    if (triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth)
    }
  }, [data, loading])

  const toggleValue = (id: string | number) => {
    const updated = selected.includes(id)
      ? selected.filter((x) => x !== id)
      : [...selected, id]
    setSelected(updated)
    field.onChange(updated)
    const selectedObjects = data.filter(
      (item) => item.id !== null && updated.includes(item.id)
    )
    changeState?.(selectedObjects)
  }

  const removeItem = (id: string | number) => {
    const updated = selected.filter((x) => x !== id)
    setSelected(updated)
    field.onChange(updated)
    const selectedObjects = data.filter(
      (item) => item.id !== null && updated.includes(item.id)
    )
    changeState?.(selectedObjects)
  }

  const selectedItems = data.filter(
    (item) => item.id !== null && selected.includes(item.id)
  )

  return (
    <div>
      <label
        className={classNames(
          'block text-sm font-medium first-letter:uppercase',
          disabled ? 'text-gray-400' : 'text-gray-700'
        )}
      >
        <span className="inline-flex items-center gap-1">
          {label}
          {
            disabled && null
            // (
            //   <svg
            //     className="h-4 w-4 text-gray-400"
            //     fill="none"
            //     stroke="currentColor"
            //     strokeWidth={2}
            //     viewBox="0 0 24 24"
            //     xmlns="http://www.w3.org/2000/svg"
            //   >
            //     <path
            //       d="M12 15v2m0-8v2m0 4v2m-6 4h12a2 2 0 002-2V7a2 2 0 00-2-2H6a2 2 0 00-2 2v10a2 2 0 002 2z"
            //       strokeLinecap="round"
            //       strokeLinejoin="round"
            //     />
            //   </svg>
            // )
          }
        </span>
      </label>

      <Popover.Root onOpenChange={setOpen} open={open}>
        <Popover.Trigger asChild disabled={disabled}>
          <button
            className={classNames(
              'relative w-full cursor-pointer rounded-md border bg-white py-2 pl-3 pr-10 text-left text-gray-500 ' +
                'shadow-sm focus:border-gray-600 focus:outline-none focus:ring-1 focus:ring-gray-500 sm:text-sm',
              disabled ? 'border-gray-300' : 'border-gray-500',
              fieldState.error
                ? 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500'
                : '',
              props.className as string
            )}
            ref={triggerRef}
            type="button"
          >
            {data.length === 0 ? (
              'No hay opciones que mostrar'
            ) : selectedItems.length === 0 ? (
              'Seleccione'
            ) : (
              <div className="flex flex-wrap gap-1">
                {selectedItems.map((item) => (
                  <span
                    className={cn(
                      'cursor-pointer select-none rounded border border-gray-300 bg-white px-2 py-0.5 text-gray-700'
                    )}
                    key={item.id}
                    onClick={(e) => {
                      e.stopPropagation()
                      if (item.id !== null) removeItem(item.id)
                    }}
                  >
                    {item.name}
                  </span>
                ))}
              </div>
            )}

            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronDownIcon className="h-5 w-5 text-gray-400" />
            </span>
          </button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            align="start"
            className={cn(
              'z-50 mt-1 max-h-60 overflow-auto rounded-md border border-gray-300 bg-white p-2 shadow-lg',
              'scrollbar-thin scrollbar-track-slate-50 scrollbar-thumb-slate-400 focus:outline-none sm:text-sm'
            )}
            onTouchMove={(e) => {
              e.stopPropagation()
            }}
            onTouchStart={(e) => {
              e.stopPropagation()
            }}
            onWheel={(e) => e.stopPropagation()}
            sideOffset={5}
            style={{ width: triggerWidth }}
          >
            {loading ? (
              <div className="flex justify-center py-2 text-gray-500">
                Cargando...
              </div>
            ) : (
              <div className="space-y-1">
                {data
                  .slice()
                  .sort((a, b) =>
                    a.name < b.name ? -1 : a.name > b.name ? 1 : 0
                  )
                  .map((item) => (
                    <label
                      className={classNames(
                        'flex cursor-pointer items-center gap-2 rounded py-2 pl-3 pr-9',
                        item.disabled
                          ? 'cursor-not-allowed text-gray-700'
                          : 'hover:bg-indigo-600 hover:text-white',
                        item.id !== null && selected.includes(item.id)
                          ? 'bg-indigo-100 font-semibold'
                          : 'font-normal'
                      )}
                      key={item.id}
                    >
                      <Checkbox.Root
                        checked={item.id !== null && selected.includes(item.id)}
                        className={classNames(
                          'flex h-4 w-4 items-center justify-center rounded border border-gray-400',
                          item.id !== null && selected.includes(item.id)
                            ? 'border-indigo-600 bg-indigo-600'
                            : 'bg-white'
                        )}
                        disabled={item.disabled || disabled}
                        onCheckedChange={() => {
                          if (!item.disabled && item.id !== null)
                            toggleValue(item.id)
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox.Indicator>
                          <CheckIcon className="h-3 w-3 text-white" />
                        </Checkbox.Indicator>
                      </Checkbox.Root>
                      <span
                        className={classNames(
                          item.disabled ? 'text-gray-300' : '',
                          'truncate'
                        )}
                      >
                        {item.name}
                      </span>
                    </label>
                  ))}
              </div>
            )}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {fieldState.error && (
        <p className="mt-1 text-xs text-red-600">{fieldState.error.message}</p>
      )}
    </div>
  )
}
